import React, { useEffect, useState } from 'react';
import GoogleMapReact from 'google-map-react';
import { connect } from 'react-redux'

import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import IconButton from '@material-ui/core/IconButton';
import LocationSearchingIcon from '@material-ui/icons/LocationSearching';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import LinearProgress from '@material-ui/core/LinearProgress';
import IosFunnel from 'react-ionicons/lib/IosFunnel'
import Popover from '@material-ui/core/Popover';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import ListSubheader from '@material-ui/core/ListSubheader';

import RoomIcon from '@material-ui/icons/Room';
import { makeStyles } from '@material-ui/core/styles';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import axios from 'axios';
import { markerImgs } from './markers/images';
import _ from 'lodash';

import { getStations, setGeoPosition, resetStation, getStation, getFilterData, getPlannedStations } from '../../actions';
import { REGION_HOME, GOOGLE_PLACES_API_KEY, MIDDLEWARE_API_URL, PROVIDER } from '../../utils/settings';

import HomeMarker from './markers/home';
import DetailDrawer from './details/details';
import FilterDrawer from './filter/filter';


const useStyles = makeStyles({
  option: {
    fontSize: 15,
    '& > span': {
      marginRight: 10,
      fontSize: 18,
    },
  },
  acRoot: {
    width: 300,
    ['@media (max-width:600px)']: { // eslint-disable-line no-useless-computed-key
      width: 200
    }
  },
  acInput: {
    color: '#ffffff',
    padding: 0,
    lineHeight: 1,
    margin: 0,
    borderColor: '#ffffff'
  },
  acInputLabel: {
    color: '#ffffff',
    padding: 0,
    margin: 0
  },
  acInputLabelformControl: {
    color: '#ffffff'
  },
  acInputLabelOutlined: {
    transform: 'translate(10px, 10px) scale(1)',
    borderColor: '#ffffff'
  }
});


function Map({ stations, plannedStations, loading, position, getStations, setGeoPosition, resetStation, getStation, filter, getFilterData, getPlannedStations }) {

  const classes = useStyles();

  const [mapRef, setMapRef] = useState(null);
  const [openDetails, setOpenDetails] = useState(false);
  const [openPlannedDetails, setOpenPlannedDetails] = useState(false);
  const [selectedPlannedStation, setSelectedPlannedStation] = useState(null);
  const [openFilter, setOpenFilter] = useState(false);
  const urlParams = new URL(document.location).searchParams;
  const urlLat = urlParams.get('lat');
  const urlLng = urlParams.get('lng');
  const urlZoom = urlParams.get('zoom');
  const initialCenter = urlLat && urlLng
    ? { lat: parseFloat(urlLat), lng: parseFloat(urlLng) }
    : { lat: REGION_HOME.latitude, lng: REGION_HOME.longitude };
  const initialZoom = urlZoom ? parseInt(urlZoom, 10) : 15;
  const [center, setCenter] = useState(initialCenter);
  const [acOptions, setAcOptions] = useState([]);
  const [gMapObj, setGMapObj] = useState([null, null]);
  const [markers, setMarkers] = useState([]);
  const markerMapRef = React.useRef({});
  const plannedMarkerMapRef = React.useRef({});


  useEffect(() => {
    if (mapRef) {
      const bounds = [mapRef.prevBounds_[1], mapRef.prevBounds_[2], mapRef.prevBounds_[3], mapRef.prevBounds_[0]];
      getStations(bounds, filter, position);
      getPlannedStations(bounds, filter);
    }
  }, [filter, getStations])

  useEffect(() => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition((position) => {
        setGeoPosition(position);
        if (!urlLat || !urlLng) {
          setCenter({ lat: position.coords.latitude, lng: position.coords.longitude });
        }
        if (mapRef) {
          getStations([mapRef.prevBounds_[1], mapRef.prevBounds_[2], mapRef.prevBounds_[3], mapRef.prevBounds_[0]], filter, position);
        }
      }, (error) => {
        setGeoPosition({ coords: { latitude: REGION_HOME.latitude, longitude: REGION_HOME.longitude } });
      });
    } else {
      if (mapRef) {
        getStations([mapRef.prevBounds_[1], mapRef.prevBounds_[2], mapRef.prevBounds_[3], mapRef.prevBounds_[1]], filter, position);
      }
    }
    getFilterData();
  }, []);

  useEffect(() => {
    prepareMarker();
  }, [stations]);

  let prepareMarker = () => {
    if (!gMapObj[0]) return;
    if (!stations || !stations.stations) return;

    const currentUuids = new Set(stations.stations.map(s => s.uuid));

    // Remove markers no longer in the result
    Object.keys(markerMapRef.current).forEach(uuid => {
      if (!currentUuids.has(uuid)) {
        markerMapRef.current[uuid].setMap(null);
        delete markerMapRef.current[uuid];
      }
    });

    if (!loading && stations.station_count > 0) {
      stations.stations.forEach(station => {
        if (markerMapRef.current[station.uuid]) return; // already exists

        let icon = {
          url: markerImgs[station.group],
          scaledSize: new gMapObj[1].Size(30, 30),
          origin: new gMapObj[1].Point(0, 0),
          anchor: new gMapObj[1].Point(0, 0)
        };

        var location = randomizeCoords(station.location.latitude, station.location.longitude, station.label.length % 10);

        let marker = new gMapObj[1].Marker({
          position: { lat: location.latitude, lng: location.longitude },
          icon: icon,
          uuid: station.uuid,
          map: gMapObj[0]
        });

        marker.addListener('click', function () {
          handleOpenDetails(marker.uuid);
        });

        markerMapRef.current[station.uuid] = marker;
      });
    }
  };

  function randomizeCoords(lat, lon, m) {
    var R = 6378137;

    var dn = m;
    var de = m;

    var dLat = dn / R;
    var dLon = de / (R * Math.cos(Math.PI * lat / 180));

    var latO = lat + dLat * 180 / Math.PI;
    var lonO = lon + dLon * 180 / Math.PI;

    return {
      latitude: latO,
      longitude: lonO
    };
  }

  function renderFiltersActive() {
    var active = false;

    if (filter.hasOwnProperty('connectors') || filter.hasOwnProperty('groups') || filter.hasOwnProperty('power')) {
      filter.connectors.forEach(connector => {
        if (connector.active === false) {
          active = true;
        }
      });

      filter.groups.forEach(group => {
        if (group.name !== 'est_offline' && group.name !== 'others_offline' && group.name !== 'egg_offline') {
          if (group.active === false) {
            active = true;
          }
        }
      });

      if (filter.power && (filter.power.to && filter.power.from)) {
        if (filter.power.from !== 0 || filter.power.to !== 350000) {
          active = true;
        }
      }

      if (active) {
        return (
          <div style={{ position: 'absolute', top: 100, display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'center', zIndex: 999, cursor: 'pointer' }} onClick={() => { setOpenFilter(true) }}>
            <span style={{ fontSize: 12, color: '#ffffff', padding: 5, backgroundColor: '#999999', borderRadius: 6 }}><IosFunnel color={'white'} fontSize={'12'} /> Filter aktiv</span>
          </div>
        )
      }
    }
  }

  useEffect(() => {
    if (gMapObj[0]) {
      prepareMarker();
    }
  }, [gMapObj[0]]);

  useEffect(() => {
    preparePlannedMarker();
  }, [plannedStations]);

  let preparePlannedMarker = () => {
    if (!gMapObj[0]) return;
    if (PROVIDER !== 'EST') return;
    if (!plannedStations || !Array.isArray(plannedStations) || plannedStations.length === 0) return;

    const stationKey = (s) => `${s.name}_${s.location.latitude}_${s.location.longitude}`;
    const currentKeys = new Set(plannedStations.map(stationKey));

    Object.keys(plannedMarkerMapRef.current).forEach(key => {
      if (!currentKeys.has(key)) {
        plannedMarkerMapRef.current[key].setMap(null);
        delete plannedMarkerMapRef.current[key];
      }
    });

    plannedStations.forEach(station => {
      const key = stationKey(station);
      if (plannedMarkerMapRef.current[key]) return;

      const overlay = new gMapObj[1].OverlayView();
      overlay.position = new gMapObj[1].LatLng(station.location.latitude, station.location.longitude);

      overlay.onAdd = function () {
        const wrapper = document.createElement('div');
        wrapper.className = 'planned-station-marker';

        const svgNS = 'http://www.w3.org/2000/svg';
        const svg = document.createElementNS(svgNS, 'svg');
        svg.setAttribute('width', '40');
        svg.setAttribute('height', '40');
        svg.setAttribute('viewBox', '0 0 40 40');
        svg.style.position = 'absolute';
        svg.style.top = '0';
        svg.style.left = '0';

        const safeKey = key.replace(/[^a-zA-Z0-9]/g, '_');
        const defs = document.createElementNS(svgNS, 'defs');

        const clipPath = document.createElementNS(svgNS, 'clipPath');
        clipPath.setAttribute('id', `clip-${safeKey}`);
        const clipCircle = document.createElementNS(svgNS, 'circle');
        clipCircle.setAttribute('cx', '20');
        clipCircle.setAttribute('cy', '20');
        clipCircle.setAttribute('r', '20');
        clipPath.appendChild(clipCircle);
        defs.appendChild(clipPath);
        svg.appendChild(defs);

        const g = document.createElementNS(svgNS, 'g');
        g.setAttribute('clip-path', `url(#clip-${safeKey})`);

        const bgRect = document.createElementNS(svgNS, 'rect');
        bgRect.setAttribute('width', '40');
        bgRect.setAttribute('height', '40');
        bgRect.setAttribute('fill', '#ff8800');
        g.appendChild(bgRect);

        for (let i = -40; i <= 80; i += 12) {
          const line = document.createElementNS(svgNS, 'line');
          line.setAttribute('x1', String(i));
          line.setAttribute('y1', '0');
          line.setAttribute('x2', String(i + 40));
          line.setAttribute('y2', '40');
          line.setAttribute('stroke', '#000000');
          line.setAttribute('stroke-width', '4');
          g.appendChild(line);
        }

        svg.appendChild(g);
        wrapper.appendChild(svg);

        const img = document.createElement('img');
        img.src = markerImgs['est'];
        img.style.width = '30px';
        img.style.height = '30px';
        img.style.borderRadius = '50%';
        img.style.position = 'absolute';
        img.style.top = '5px';
        img.style.left = '5px';
        wrapper.appendChild(img);

        wrapper.addEventListener('click', () => {
          setSelectedPlannedStation(station);
          setOpenPlannedDetails(true);
        });
        this.div = wrapper;
        this.getPanes().overlayMouseTarget.appendChild(wrapper);
      };

      overlay.draw = function () {
        const point = this.getProjection().fromLatLngToDivPixel(this.position);
        if (point) {
          this.div.style.position = 'absolute';
          this.div.style.left = (point.x - 20) + 'px';
          this.div.style.top = (point.y - 20) + 'px';
        }
      };

      overlay.onRemove = function () {
        if (this.div && this.div.parentNode) {
          this.div.parentNode.removeChild(this.div);
          this.div = null;
        }
      };

      overlay.setMap(gMapObj[0]);
      plannedMarkerMapRef.current[key] = overlay;
    });
  };

  let onMapChange = (map) => {
    const bounds = [map.bounds.sw.lng, map.bounds.se.lat, map.bounds.ne.lng, map.bounds.nw.lat];
    getStations(bounds, filter);
    getPlannedStations(bounds, filter);
    const url = new URL(document.location);
    url.searchParams.set('lat', map.center.lat.toFixed(6));
    url.searchParams.set('lng', map.center.lng.toFixed(6));
    url.searchParams.set('zoom', map.zoom);
    window.history.replaceState(null, '', url.toString());
  }

  const toggleDetailsDrawer = (side, open) => event => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }

    if (!open) resetStation();

    setOpenDetails(open)
  };

  let handleOpenDetails = (uuid) => {
    if (uuid === 'home') return false;
    getStation(uuid);
    setOpenDetails(true);
  }

  const toggleFilterDrawer = (side, open) => event => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }

    setOpenFilter(open)
  };

  let handleCenter = () => {
    if (position) {
      var latLng = new gMapObj[1].LatLng(position.coords.latitude, position.coords.longitude);
      gMapObj[0].setZoom(17);
      gMapObj[0].panTo(latLng);
      const url = new URL(document.location);
      url.searchParams.delete('lat');
      url.searchParams.delete('lng');
      window.history.replaceState(null, '', url.toString());
    }
  };

  const doAcLookup = (e) => {
    setAcOptions([]);
    let input = e.target.value;

    if (input.length < 1 || input === 0) return false;

    setTimeout(() => {
      var places = [];

      axios.get(MIDDLEWARE_API_URL + 'google/places/?input=' + input)
        .then(response => {
          response.data.result.predictions.map(place => {
            return places.push({ type: 'google', title: place.description, placeId: place.place_id });
          });
          setAcOptions(_.union(acOptions, places))
        })
        .finally(() => {
          var groups = '';

          var estOfflineId = null;
          var eggOfflineId = null;
          var othersOfflineId = null;

          for (var group of filter.groups) {
            if (group.name === 'est_offline')
              estOfflineId = group.id
            else if (group.name === 'egg_offline')
              eggOfflineId = group.id
            else if (group.name === 'others_offline')
              othersOfflineId = group.id

            if (group.active) {
              groups += group.id + ',';

              if (group.name === 'est' && estOfflineId !== null) {
                groups += estOfflineId + ',';
              }
              if (group.name === 'est' && eggOfflineId !== null) {
                groups += eggOfflineId + ',';
              }
              if (group.name === 'egg' && eggOfflineId !== null) {
                groups += eggOfflineId + ',';
              }
              if (group.name === 'egg' && estOfflineId !== null) {
                groups += estOfflineId + ',';
              }
              if (group.name === 'others' && othersOfflineId !== null) {
                groups += othersOfflineId + ',';
              }
            }
          }

          var connectors = '';
          for (var connector of filter.connectors) {
            if (connector.active) {
              connectors += connector.icon + ',';
            }
          }

          connectors = connectors.replace(/,\s*$/, '');
          groups = groups.replace(/,\s*$/, '');

          axios.get(MIDDLEWARE_API_URL + 'stations/bylabel/?label=' + input + '&groups=' + groups + '&connectors=' + connectors + '&power_from=' + filter.power.from + '&power_to=' + filter.power.to + '&provider=' + PROVIDER)
            .then(response => {
              if (response.data && response.data.result.stations) {
                response.data.result.stations.map((station) => {
                  return places.push({ type: 'station', title: station.label, location: station.location, group: station.group });
                })
              }

              setAcOptions(_.union(acOptions, places))
            })
            .then(() => {
              axios.get(MIDDLEWARE_API_URL + 'stations/byevseid/?evseid=' + input + '&provider=' + PROVIDER)
                .then(response => {

                  if (response.data) {
                    response.data.result.stations.forEach(station => {
                      return places.push({ type: 'evseid', title: station.evseid, location: station.location, group: station.group });
                    });
                  }

                  setAcOptions(_.union(acOptions, places))

                })
            });
        });
    }, 500);
  };

  const panToAcOption = (value) => {
    if (value && value.type !== 'google') {
      var latLng = new gMapObj[1].LatLng(value.location.latitude, value.location.longitude);
      gMapObj[0].setZoom(17);
      gMapObj[0].panTo(latLng);
    } else {
      if (!value || !value.placeId) return false;
      axios.get(MIDDLEWARE_API_URL + 'google/places/' + value.placeId + '/')
        .then(response => {
          var latLng = new gMapObj[1].LatLng(response.data.result.result.geometry.location.lat, response.data.result.result.geometry.location.lng);
          gMapObj[0].setZoom(18);
          gMapObj[0].panTo(latLng);
        });
    }
  };

  const renderAcIcon = (item) => {
    if (item.type === 'google') {
      return <RoomIcon color={'primary'} />
    } else {
      return <img alt={'group'} src={markerImgs[item.group]} style={{ width: '25px', height: '25px' }} />
    }
  };

  let createMapOptions = (maps) => {

    let styles = [
      {
        'featureType': 'poi',
        'elementType': 'labels.icon',
        'stylers': [
          {
            'visibility': 'off'
          }
        ]
      },
      {
        'featureType': 'transit',
        'stylers': [
          {
            'visibility': 'off'
          }
        ]
      }
    ]

    let params = (new URL(document.location)).searchParams;
    var minZoom = 10;
    if (params.has('minZoom')) minZoom = params.get('minZoom');

    return {
      zoomControl: true,
      mapTypeControl: false,
      scaleControl: false,
      streetViewControl: false,
      rotateControl: false,
      fullscreenControl: false,
      styles: styles,
      minZoom: minZoom,
      maxZoom: 18
    }
  }

  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;


  return (
    <div id='finder-container' style={{ position: 'relative' }}>
      <AppBar position='static'>
        <Toolbar>
          <div style={{ flexGrow: 1 }}>
            <div className={'autocomplete'}>
              <Autocomplete
                onInputChange={doAcLookup}
                onChange={(e, value) => { panToAcOption(value) }}
                onClose={() => { setAcOptions([]) }}
                options={acOptions}
                classes={{
                  root: classes.acRoot,
                  option: classes.option,
                }}
                popupIcon={<ArrowDropDownIcon color={'secondary'} />}
                color={'primary'}
                groupBy={(option) => {
                  if (option.type === 'google') return 'Orte';
                  else if (option.type === 'station') return 'Ladestellen';
                  else if (option.type === 'evseid') return 'Ladepunkte';
                }}
                autoHighlight
                noOptionsText={'Ort/EVSE-ID/Ladestelle'}
                getOptionLabel={option => option.title}
                renderOption={option => (
                  <React.Fragment key={option.title}>
                    <span>{renderAcIcon(option)}</span>
                    {option.title}
                  </React.Fragment>
                )}
                renderInput={params => (
                  <TextField
                    {...params}
                    label='Suchen…'
                    variant='outlined'
                    fullWidth
                    inputProps={{
                      ...params.inputProps,
                      autoComplete: 'disabled',
                      className: classes.acInput,
                      classes: {
                        notchedOutline: classes.notchedOutline
                      }
                    }}
                    InputLabelProps={{
                      ...params.InputLabelProps,
                      classes: {
                        formControl: classes.acInputLabelformControl,
                        outlined: classes.acInputLabelOutlined
                      }
                    }}
                  />
                )}
              />
            </div>
          </div>
          <div>
            <IconButton color={'inherit'} onClick={handleClick}>
              <HelpOutlineIcon />
            </IconButton>
            <IconButton color={'inherit'} onClick={() => setOpenFilter(true)}>
              <IosFunnel color={'white'} />
            </IconButton>
            <IconButton color={'inherit'} onClick={handleCenter}>
              <LocationSearchingIcon />
            </IconButton>
          </div>
        </Toolbar>
      </AppBar>
      {loading ? <LinearProgress color={'primary'} style={{ position: 'absolute', width: '100%', zIndex: 99 }} /> : null}
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        style={{ zIndex: 9999 }}
      >
        <List dense subheader={<ListSubheader disableSticky>Legende</ListSubheader>} style={{ maxWidth: 300 }}>
          {PROVIDER === 'EGG'
            ?
            <ListItem divider key={'egg'}>
              <ListItemIcon>
                <img alt={'marker'} src={markerImgs['egg']} style={{ width: '30px', height: '30px' }} />
              </ListItemIcon>
              <ListItemText primary={'Energie Graz'} secondary={'Zertifizierte Ladestellen der Energie Graz'} />
            </ListItem>
            :
            <ListItem divider key={'est'}>
              <ListItemIcon>
                <img alt={'marker'} src={markerImgs['est']} style={{ width: '30px', height: '30px' }} />
              </ListItemIcon>
              <ListItemText primary={'Energie Steiermark'} secondary={'Zertifizierte Ladestellen der Energie Steiermark und ihrer Partner'} />
            </ListItem>
          }
          <ListItem divider key={'beoe'}>
            <ListItemIcon>
              <img alt={'marker'} src={markerImgs['beoe']} style={{ width: '30px', height: '30px' }} />
            </ListItemIcon>
            <ListItemText primary={'BEÖ'} secondary={'Ladestellen von Mitgliedern des Bundesverbandes Elektromobilität Österreich'} />
          </ListItem>
          <ListItem divider key={'roaming'}>
            <ListItemIcon>
              <img alt={'marker'} src={markerImgs['roaming']} style={{ width: '30px', height: '30px' }} />
            </ListItemIcon>
            <ListItemText primary={'Roaming'} secondary={'Ladestellen von Roaming-Partnern'} />
          </ListItem>
          <ListItem divider key={'community'}>
            <ListItemIcon>
              <img alt={'marker'} src={markerImgs['others']} style={{ width: '30px', height: '30px' }} />
            </ListItemIcon>
            <ListItemText primary={'Community'} secondary={'Nicht zertifizierte Ladestellen'} />
          </ListItem>
          <ListItem key={'egg-taxi'}>
            <ListItemIcon>
              <img alt={'marker'} src={markerImgs['egg-taxi']} style={{ width: '30px', height: '30px' }} />
            </ListItemIcon>
            <ListItemText primary={'Taxi'} secondary={'Ladestellen nur für ausgewählte Taxi-Partner'} />
          </ListItem>
        </List>
      </Popover>
      <div style={{ height: 'calc(100vh - 64px)', width: '100%' }} id={'map-container'}>
        {openDetails ? <DetailDrawer open={openDetails} toggleDetailsDrawer={toggleDetailsDrawer} /> : null}
        {openPlannedDetails ? <DetailDrawer open={openPlannedDetails} toggleDetailsDrawer={(side, open) => () => { setOpenPlannedDetails(open); if (!open) setSelectedPlannedStation(null); }} plannedStation={selectedPlannedStation} /> : null}
        {openFilter ? <FilterDrawer open={openFilter} toggleFilterDrawer={toggleFilterDrawer} /> : null}
        {renderFiltersActive()}
        <GoogleMapReact
          bootstrapURLKeys={{ key: GOOGLE_PLACES_API_KEY }}
          defaultCenter={{ lat: REGION_HOME.latitude, lng: REGION_HOME.longitude }}
          defaultZoom={initialZoom}
          center={center}
          onChange={onMapChange}
          ref={(map) => setMapRef(map)}
          onChildClick={handleOpenDetails}
          yesIWantToUseGoogleMapApiInternals
          onGoogleApiLoaded={({ map, maps }) => { setGMapObj([map, maps]); }}
          options={createMapOptions}
        >
          {position ? <HomeMarker
            lat={position.coords.latitude}
            lng={position.coords.longitude}
            key={'home'}
          /> : null}
        </GoogleMapReact>
      </div>
    </div>
  );
}

const mapStateToProps = (state) => ({
  stations: state.stations,
  plannedStations: state.planned_stations,
  loading: state.loading,
  position: state.position,
  station: state.station,
  loadingStation: state.loading_station,
  filter: state.filter
})

const mapDispatchToProps = {
  getStations: getStations,
  setGeoPosition: setGeoPosition,
  resetStation: resetStation,
  getStation: getStation,
  getFilterData: getFilterData,
  getPlannedStations: getPlannedStations
};

export default connect(mapStateToProps, mapDispatchToProps)(Map);